/* eslint-disable no-empty-pattern */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Flex, Row } from '@amzn/stencil-react-components/layout';
import { Label, Text } from '@amzn/stencil-react-components/text';
import { Select } from '@amzn/stencil-react-components/form';
import { IconQuestionCircle } from '@amzn/stencil-react-components/icons';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import {
  TableElement,
  TbodyElement,
  TdElement,
  TheadElement,
  TrElement,
} from '@amzn/stencil-react-components/table';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';
import moment from 'moment';
import { WA_WAREHOUSE_ALLOW_LIST } from 'src/constants/featureGates';
import { QualityReportItem } from 'src/types/backendService';
import { formatReportItems } from 'src/util/lookback_report_utils';

import { useGlobalContext, useUserDataContext } from '../hooks';

import { EmailReportWidget } from './shared/EmailReportWidget';

const IconWithTooltip = withTooltip()(IconQuestionCircle);

const QualityDisclosure = ({}) => (
  <Text fontWeight="light">
    If you have questions about eligibility criteria or the weekly quality review process, please
    refer to the informational available in your WorkDocs Folder in A to Z.
  </Text>
);

export const QualityLookbackReport = ({}) => {
  const { t } = useTranslation();
  const { qualityReportItems } = useUserDataContext();
  const { userDetails } = useGlobalContext();

  const qualityReportMonthAggregationMap = new Map();

  // we will show either 14 weeks or 180 days. These are either 4 or 6 full months
  const monthLookback = WA_WAREHOUSE_ALLOW_LIST.includes(userDetails.warehouseId) ? 7 : 4;
  let currentMonth = moment();
  for (let i = 0; i < monthLookback; i++) {
    qualityReportMonthAggregationMap.set(currentMonth.format('MMM YYYY'), []); // Add the formatted month to the array
    currentMonth = currentMonth.subtract(1, 'month'); // Move to the previous month
  }

  if (qualityReportItems) {
    qualityReportItems.forEach((item) => {
      const month = moment(item.periodStart! * 1000).format('MMM YYYY');
      if (qualityReportMonthAggregationMap.has(month)) {
        qualityReportMonthAggregationMap.get(month)!.push(item);
      }
    });
  }

  // Items are already sorted from newest to oldest. We will set the month to the first item
  const startingMonth = moment().format('MMM YYYY');
  const startingReportItems = qualityReportMonthAggregationMap.get(startingMonth) ?? [];
  const [selectedMonth, setSelectedMonth] = useState(startingMonth);
  const [qualityReportMonthlyItems, setQualityReportMonthlyItems] = useState<QualityReportItem[]>(startingReportItems);

  const updateState = (month: string) => {
    setSelectedMonth(month);
    setQualityReportMonthlyItems(qualityReportMonthAggregationMap.get(month));
  };

  const tableRows = useMemo(() => {
    if (qualityReportMonthlyItems) {
      const formattedReports = formatReportItems(qualityReportMonthlyItems);
      return formattedReports.map((report) => {
        const data = report.report;
        return (
          // eslint-disable-next-line react/jsx-key
          <TrElement>
            {report.spanningRow && (
              <TdElement rowSpan={report.rowSpan}>
                {moment(data.periodStart! * 1000).format('M/D/Y')}
              </TdElement>
            )}
            {report.spanningRow && (
              <TdElement rowSpan={report.rowSpan}>
                {moment(data.periodStart! * 1000)
                  .add(7, 'days')
                  .format('M/D/Y')}
              </TdElement>
            )}
            <TdElement>{data.errorFamily}</TdElement>
            <TdElement>{data.errorType ? data.errorType : '-'}</TdElement>
            <TdElement>{data.unitsProcessed}</TdElement>
            <TdElement>{data.errors}</TdElement>
            <TdElement>{data.dpmo ? Math.round(data.dpmo) : '0'}</TdElement>
            <TdElement>{data.expectedDpmo}</TdElement>
            {report.spanningRow && (
              <TdElement rowSpan={report.rowSpan}>
                {data.percentToPeers ? `${Math.round(data.percentToPeers)}%` : ''}
              </TdElement>
            )}
          </TrElement>
        );
      });
    }
    return [];
  }, [qualityReportMonthlyItems, qualityReportItems]);

  if (qualityReportItems === undefined) {
    return (
      <Flex
        height="188px"
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
      >
        <Spinner showText />
      </Flex>
    );
  }
  return (
    <Col gridGap="S200">
      <QualityDisclosure />
      <Flex
        flexDirection="row"
        justifyContent="right"
        alignItems="center"
        gridGap="S300"
      >
        <Col
          alignItems="start"
          gridGap="S300"
        >
          <Label
            htmlFor="week-selector"
            id="week-selector-label"
          >
            <Text
              fontSize="T200"
              fontWeight="light"
              textAlign="right"
            >
              {t('month')}
            </Text>
          </Label>
        </Col>
        <Col width={150}>
          <Select
            labelId="week-selector-label"
            id="week-selector"
            value={selectedMonth}
            onChange={(value) => updateState(value)}
            options={Array.from(qualityReportMonthAggregationMap.keys())}
          />
        </Col>
        <Col>
          <EmailReportWidget reportType="FourteenWeekQualityReport" />
        </Col>
      </Flex>
      {qualityReportMonthlyItems.length === 0 ?
        <Text fontWeight="light">No data available.</Text> :
        <Col>
          <TableElement>
            <TheadElement>
              <TrElement>
                <TdElement>Period Start</TdElement>
                <TdElement>Period End</TdElement>
                <TdElement>Error Family</TdElement>
                <TdElement>Error Type</TdElement>
                <TdElement>Units Processed</TdElement>
                <TdElement>Number of Errors</TdElement>
                <TdElement>
                  <Row alignItems="center">
                    Your DPMO
                    <IconWithTooltip tooltipText="Total number of errors / total number of units processed * 1,000,000 within the particular Error Family" />
                  </Row>
                </TdElement>
                <TdElement>
                  <Row alignItems="center">
                    Threshold DPMO
                    <IconWithTooltip tooltipText="The DPMO that 75% of eligible associates who worked in the process path were able to achieve in the prior month" />
                  </Row>
                </TdElement>
                <TdElement>
                  <Row alignItems="center">
                    Your combined % to peers
                    <IconWithTooltip tooltipText="How your performance ranked compared to your peers across all process paths" />
                  </Row>
                </TdElement>
              </TrElement>
            </TheadElement>
            <TbodyElement>{tableRows}</TbodyElement>
          </TableElement>
        </Col>}
    </Col>
  );
};
